import React from 'react';
import SprayFoamCoverage from './coverage-widgets/SprayFoamCoverage';
import RValueCoverage from './coverage-widgets/RValueCoverage';
import AtticWallToggle from './coverage-widgets/AtticWallToggle';

/**
 * ItemCoveragePanel — Iter18 R2 §1.5.1
 * ---------------------------------------------------------------------------
 * Per-item coverage widget mounted INSIDE the Quote-Items Item Editor
 * (replaces the standalone /settings/coverage-charts page — retired in E.2).
 *
 * Dispatches on the item's category (categoryId from seedCatalog) or falls
 * back to itemType. Three modes, per the 06-25 stakeholder walkthrough:
 *
 *   • Spray foam (open/closed cell)     → SprayFoamCoverage  (auto-calc grid)
 *   • Blown-in (fiberglass|cellulose|
 *     mineral wool) — ALL blow          → AtticWallToggle
 *                                         wrapping RValueCoverage (dual grids)
 *   • Batts (any faced/unfaced/FSK/foil) → RValueCoverage      (single grid)
 *   • Anything else (accessories …)      → panel hidden entirely.
 *
 * Values persist on the item record via the coverage_* fields (see
 * QuoteItemsPage save path). Schema:
 *
 *   coverage_mode:        'sprayfoam' | 'rvalue' | 'rvalue-attic-wall' | 'none'
 *   coverage_per_set?:            number           (sprayfoam)
 *   coverage_max_thickness_in?:   number (14.5)    (sprayfoam, per-item)
 *   coverage_rvalues?:            Record<R, number|null>   (batts single grid)
 *   coverage_rvalues_attic?:      Record<R, number|null>   (all blow — attic)
 *   coverage_rvalues_wall?:       Record<R, number|null>   (all blow — wall)
 *
 * Scope fence: This is per-tenant per-item editing on /settings/quote-items.
 * The separate SA-9 Super-Admin "Coverage-Chart Baseline" (tenant-seed
 * defaults) ships in Iter19 and re-uses this component tree.
 */

// Category-id → coverage mode. Sourced from bidit/frontend/src/quote-builder/seedCatalog.js
// PRODUCT_CATEGORIES: cc_spray_foam, oc_spray_foam, fg_blow, cellulose_blow,
// min_wool_blow, kraft_batts, unfaced_batts, foil_batts, fsk_batts, plus
// accessories / light_covers / others (→ none).
export function resolveCoverageMode(categoryId, itemType) {
  if (!categoryId && !itemType) return 'none';
  const cat = String(categoryId || '').toLowerCase();

  // Spray foam — auto-calc grid, no attic/wall split
  if (cat.includes('spray_foam')) return 'sprayfoam';

  // All blow categories — dual attic/wall grids (Q10 answer 2026-07-17)
  if (cat.includes('_blow') || cat === 'blowing_wool') return 'rvalue-attic-wall';

  // Batts — single R-value grid (batts are pre-cut, not application-dependent)
  if (cat.includes('batts') || cat.includes('batt')) return 'rvalue';

  // Fallback via itemType (for hand-authored items without a categoryId)
  const t = String(itemType || '').toLowerCase();
  if (t === 'spray_foam') return 'sprayfoam';
  if (t === 'blown_insulation') return 'rvalue-attic-wall';

  return 'none';
}

export default function ItemCoveragePanel({ item, onChange }) {
  const mode = resolveCoverageMode(item?.categoryId, item?.itemType);
  if (mode === 'none') return null;

  const patch = (partial) => onChange({ ...item, ...partial });

  return (
    <section
      className="rounded-md border p-4"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-surface-sunken)',
      }}
    >
      <header className="mb-3">
        <h4
          className="text-sm font-semibold"
          style={{ color: 'var(--color-text-default)' }}
        >
          Coverage
        </h4>
        <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
          {mode === 'sprayfoam' &&
            'Enter one Coverage Per Set value at 1" thickness. The grid auto-calculates coverage across thickness steps.'}
          {mode === 'rvalue' &&
            'Enter coverage (sq ft per bag/unit) for each R-value this product achieves. Leave blank for R-values the product doesn’t reach.'}
          {mode === 'rvalue-attic-wall' &&
            'Blowing-wool coverage differs by application. Enter values for both attic and wall installations.'}
        </p>
      </header>

      {mode === 'sprayfoam' && (
        <SprayFoamCoverage
          coveragePerSet={item.coverage_per_set ?? ''}
          maxThicknessIn={item.coverage_max_thickness_in ?? 14.5}
          onChange={patch}
        />
      )}
      {mode === 'rvalue' && (
        <RValueCoverage
          values={item.coverage_rvalues || {}}
          onChange={(coverage_rvalues) => patch({ coverage_rvalues })}
        />
      )}
      {mode === 'rvalue-attic-wall' && (
        <AtticWallToggle
          atticValues={item.coverage_rvalues_attic || {}}
          wallValues={item.coverage_rvalues_wall || {}}
          onChange={({ attic, wall }) =>
            patch({
              coverage_rvalues_attic: attic,
              coverage_rvalues_wall: wall,
            })
          }
        />
      )}
    </section>
  );
}
